import React, { useState, useEffect, useContext, createContext } from 'react';

import AuthClient from '../utils/auth-client';

import { getMe } from '../../api/users';
import { roles, getRole } from '../../app/utils/roles';
import { getLabelAddress } from '../../api/labelAddress';

const authContext = createContext();

const allowedRoles = [
  'admin',
  'warehouse_scan',
  'admin_last_mile',
  'admin_consolidated',
  'warehouse_guy',
  'assistant_picking',
  'root',
  'finance',
  'user_client',
  'admin_finance',
  'operator',
  'supervisor',
  'client_service',
  'kam',
  'coordinator'
];

const allowedUsers = roles.filter(role => {
  return allowedRoles.some(e => getRole(e).code === role.code);
});

function useProvideAuth() {
  const [isLoggedIn, setLoggedIn] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const login = async (email, password) => {
    const user = await AuthClient.login(email.toLowerCase().trim(), password);
    setLoggedIn(true);
    setUserInfo(user);
  };

  const register = async (email, password, firstName, lastName) => {
    await AuthClient.register(email, password, firstName, lastName);
  };

  const forgotCode = async email => {
    await AuthClient.forgotPassword(email);
  };

  const forgotConfirm = async (email, code, newPassword) => {
    AuthClient.confirmNewPassword(email, code, newPassword);
  };

  const logout = async () => {
    await AuthClient.logout();
    setUserInfo(false);
    setLoggedIn(false);
    setIsLoading(false);
  };

  const confirmCode = async (email, code) => {
    await AuthClient.confirmRegister(email, code);
  };

  useEffect(() => {
    const ac = new AbortController();
    async function setUp() {
      try {
        const authF = AuthClient.isLoggedIn();
        if (!isLoggedIn) {
          setIsLoading(true);
          (await authF).onAuthStateChanged(async user => {
            if (user) {
              const labelAddress = await getLabelAddress();

              if (labelAddress.id) {
                localStorage.setItem(
                  'labelAddress',
                  JSON.stringify(labelAddress)
                );
              }

              const userData = await getMe();
              const userFinal = { ...user };

              if (userData.id) {
                localStorage.setItem('userData', JSON.stringify(userData));
              }

              if (userFinal && userData) {
                userFinal.role = userData.role;
                userFinal.country_id = userData.country_id;
                userFinal.country_name = userData['Country.name'] || '';
              }

              const allowed = allowedUsers.some(e => {
                return e.code === userFinal.role;
              });

              if (!allowed) {
                setLoggedIn(false);
                setUserInfo(null);
                logout();
                return;
              }

              setUserInfo(userFinal);
              setLoggedIn(true);
            } else {
              setUserInfo(user);
              setLoggedIn(false);
            }
            setIsLoading(false);
          });
        } else {
          setIsLoading(true);
          (await authF).onAuthStateChanged(async user => {
            if (user) {
              setLoggedIn(true);
            } else {
              setLoggedIn(false);
            }
            setIsLoading(false);
          });
        }
      } catch (e) {
        setLoggedIn(false);
        setIsLoading(false);
      }
    }
    setUp();

    return () => ac.abort();
  }, [isLoggedIn, userInfo]);

  return {
    isLoggedIn,
    login,
    register,
    logout,
    forgotCode,
    forgotConfirm,
    confirmCode,
    userInfo,
    isLoading
  };
}

// eslint-disable-next-line react/prop-types
export function AuthProvider({ children }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(authContext);
  if (context === undefined) {
    throw Error(`useAuth must be used within a ProvideAuth`);
  }
  return useContext(authContext);
};
